import { useCallback, useEffect, useState } from 'react';

// Available networks. `custom` lets the user paste any RPC URL.
export type Network = 'mainnet-beta' | 'atlasnet' | 'devnet' | 'custom';

interface NetworkPreset {
  label: string;
  defaultRpc: string;
  cluster: 'mainnet-beta' | 'devnet' | 'testnet'; // for tx confirmation, etc.
}

// Env-var overrides (set in .env.local, matching landing's convention)
// fall through to the hardcoded public defaults if unset. Read at
// module-load via webpack DefinePlugin — values inlined at build time.
const PRESETS: Record<Exclude<Network, 'custom'>, NetworkPreset> = {
  'mainnet-beta': {
    label: 'Mainnet',
    defaultRpc: process.env.VITE_MAINNET_RPC_URL || 'https://api.mainnet-beta.solana.com',
    cluster: 'mainnet-beta',
  },
  atlasnet: {
    label: 'Atlasnet',
    defaultRpc: process.env.VITE_ATLASNET_RPC_URL || 'https://atlasnet.staratlas.com',
    cluster: 'mainnet-beta',
  },
  devnet: {
    label: 'Devnet',
    defaultRpc: 'https://api.devnet.solana.com',
    cluster: 'devnet',
  },
};

const STORAGE_KEY = 'playground:network-config';

interface PersistedConfig {
  network: Network;
  customRpcUrl: string;
  /**
   * Whether the SDK's singleton `audit.onSrslyEvent` listener is active.
   * Persisted so the toggle survives reloads. Defaults to `true` — most
   * folks want to see live events; turning it off only matters if the WS
   * activity is noisy or the user is on a flaky RPC.
   */
  eventsEnabled: boolean;
}

// Networks the bar still surfaces. atlasnet / devnet were retired from
// the UI — the SDK can still detect them via genesis-hash when a user
// pastes that RPC into Custom, so no functionality is lost.
const VISIBLE_NETWORKS: Network[] = ['mainnet-beta', 'custom'];

function migrate(network: Network): Network {
  return VISIBLE_NETWORKS.includes(network) ? network : 'mainnet-beta';
}

function readPersisted(): PersistedConfig {
  const fallback: PersistedConfig = {
    network: 'mainnet-beta',
    customRpcUrl: '',
    eventsEnabled: true,
  };
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedConfig>;
      return {
        network: migrate((parsed.network as Network) ?? 'mainnet-beta'),
        customRpcUrl: parsed.customRpcUrl ?? '',
        eventsEnabled: parsed.eventsEnabled ?? true,
      };
    }
  } catch {
    /* fall through to default */
  }
  return fallback;
}

export interface NetworkConfig {
  network: Network;
  /** Empty string when Custom is selected with no URL entered. */
  rpcUrl: string;
  /** False when `rpcUrl` is empty — nothing can be fetched or sent yet. */
  rpcReady: boolean;
  customRpcUrl: string;
  presets: typeof PRESETS;
  setNetwork: (next: Network) => void;
  setCustomRpcUrl: (next: string) => void;
  eventsEnabled: boolean;
  setEventsEnabled: (next: boolean) => void;
}

export function useNetworkConfig(): NetworkConfig {
  const [{ network, customRpcUrl, eventsEnabled }, setState] =
    useState<PersistedConfig>(readPersisted);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ network, customRpcUrl, eventsEnabled }));
    } catch {
      /* private browsing — silent */
    }
  }, [network, customRpcUrl, eventsEnabled]);

  const setNetwork = useCallback(
    (next: Network) => setState(prev => ({ ...prev, network: next })),
    []
  );
  const setCustomRpcUrl = useCallback(
    (next: string) => setState(prev => ({ ...prev, customRpcUrl: next })),
    []
  );
  const setEventsEnabled = useCallback(
    (next: boolean) => setState(prev => ({ ...prev, eventsEnabled: next })),
    []
  );

  // Empty when Custom is selected but no URL has been entered — deliberately
  // *not* backfilled with a preset. It used to fall through to atlasnet, a
  // cluster the bar no longer even offers, so the SDK was quietly pointed at
  // the wrong chain while the UI showed "Set RPC URL". Every lookup then came
  // back empty and read as a missing account. Callers gate on `rpcReady`.
  const rpcUrl = network === 'custom' ? customRpcUrl.trim() : PRESETS[network].defaultRpc;

  return {
    network,
    rpcUrl,
    rpcReady: rpcUrl.length > 0,
    customRpcUrl,
    presets: PRESETS,
    setNetwork,
    setCustomRpcUrl,
    eventsEnabled,
    setEventsEnabled,
  };
}
